import compact from 'lodash/compact'
import filter from 'lodash/filter'
import find from 'lodash/find'
import first from 'lodash/first'
import isObject from 'lodash/isObject'
import isString from 'lodash/isString'
import map from 'lodash/map'
import { useMemo } from 'react'

import {
  Field,
  FieldsFragment,
  JsonField,
  JSONType,
  Link,
  LinkField,
  Maybe,
  MultipleLinkField,
  ObjectField,
  Page,
  ProductField,
  Reference,
  ReferenceField,
  RteField,
  Scalars,
  Wordbook,
} from '@Generated/graphql'
import { useCheapestShippingPrice, useFreeShippingThreshold } from '@Globals/Metadata/Hooks'

import { isExternalLinkReference, isPageReference } from './RTEReferences'

type NavigationField = Extract<FieldsFragment, { __typename: 'NavigationField' }>
type NavigationItem = NonNullable<NavigationField['navigation']>[0]

const isJSONField = (field: Field | undefined): field is JsonField => field?.__typename === 'JSONField'
const isObjectField = (field: Field | undefined): field is ObjectField => field?.__typename === 'ObjectField'
const isReferenceField = (field: Field | undefined): field is ReferenceField => field?.__typename === 'ReferenceField'
const isMultipleLinkField = (field: Field | undefined): field is MultipleLinkField => field?.__typename === 'MultipleLinkField'
const isNavigationield = (field: Field | undefined): field is NavigationField => field?.__typename === 'NavigationField'
const isLinkField = (field: Field | undefined): field is LinkField => field?.__typename === 'LinkField'
const isProductField = (field: Field | undefined): field is ProductField => field?.__typename === 'ProductField'
const isWordbook = (reference: Reference | undefined): reference is Wordbook => reference?.__typename === 'Wordbook'
const isRTEField = (field: Field | undefined): field is RteField => field?.__typename === 'RTEField'

export const getRTEFieldValue = (fields: Field[], key: string) => {
  const field = find(fields, ['key', key])
  return isRTEField(field) ? field : null
}

export const getJSONFieldValue = <T extends Scalars['JSON']>(fields: Field[], key: string) => {
  const field = find(fields, ['key', key])
  return isJSONField(field) ? field.value as T : null
}

export const getObjectFieldValue = (fields: Field[], key: string) => {
  const field = find(fields, ['key', key])
  return isObjectField(field) ? (field.fields || []) : null
}

export const getReferenceFieldValue = <T extends Reference>(fields: Field[], key: string) => {
  const field = find(fields, ['key', key])
  return isReferenceField(field) ? (field.references || []) as T[] : null
}

export const getMultipleLinkFieldValue = <T extends Link>(fields: Field[], key: string) => {
  const field = find(fields, ['key', key])
  return isMultipleLinkField(field) ? (field.links || []) as T[] : null
}

export const getNavigationFieldItems = <T extends NavigationItem>(fields: Field[], key: string) => {
  const field = find(fields, ['key', key])
  return isNavigationield(field) ? (field.navigation || []) as T[] : null
}

export const getLinkFieldValue = (fields: Field[], key: string) => {
  const field = find(fields, ['key', key])
  return isLinkField(field) ? field : null
}

export const getLinkType = (link: LinkField) => {
  const reference = first(link?.references)
  if (isPageReference(reference)) {
    return reference.type
  }
  if (isExternalLinkReference(reference)) {
    return 'external'
  }
  return reference?.__typename || ''
}

export const getProductFieldValue = (fields: Field[], key: string) => {
  const field = find(fields, ['key', key])
  if (isProductField(field) && field.product) {
    return field.product
  }
  return null
}

export const getProductReferenceValue = (fields: Field[], key: string) => {
  const referenceField = getReferenceFieldValue<Page>(fields, key)

  if (!referenceField) {
    return []
  }

  return compact(
    map(
      referenceField,
      x => getProductFieldValue(x.fields || [], 'product'),
    ),
  )
}

export const getPageReferences = (references: Reference[]): Page[] => filter(references, isPageReference)

export function useDynamicVariables() {
  const cheapestShippingPrice = useCheapestShippingPrice()
  const freeShippingThreshold = useFreeShippingThreshold()

  return useMemo(() => {
    const dynamicVariables = {}

    if (cheapestShippingPrice?.format) {
      dynamicVariables['store.cheapest_shipping_price'] = cheapestShippingPrice.format
    }

    if (freeShippingThreshold?.format) {
      dynamicVariables['store.free_shipping_threshold'] = freeShippingThreshold.format
    }

    return dynamicVariables
  }, [cheapestShippingPrice?.format, freeShippingThreshold?.format])
}

export function parseExtraColors<
  const C extends readonly string[],
  R = { [K in C[number]]: string },
>(input: Maybe<JSONType> | undefined, colors: C): R {
  const obj = isObject(input) ? input : {}
  const result = {} as R

  colors.forEach((color) => {
    const value = obj[color]
    result[color] = value && isString(value) ? value : 'transparent'
  })

  return result
}

export const getLinkData = (link: Maybe<LinkField> | undefined) => {
  const reference = first(link?.references)

  let title = ''
  let type = ''

  if (isPageReference(reference)) {
    title = reference.title || ''
    type = reference.type
  } else if (isExternalLinkReference(reference)) {
    type = 'external'
  } else if (reference?.__typename) {
    type = reference.__typename
  }

  const referenceID = reference && 'id' in reference ? reference.id || '' : ''

  return {
    id: referenceID,
    title,
    type,
  }
}

export const getLinkFromReference = (reference: any | null | undefined) => {
  if (!reference) {
    return null
  }

  let text = ''
  let type = ''
  let url = ''

  if (isPageReference(reference)) {
    text = reference.title || ''
    type = reference.type
    url = reference.url || ''
  } else if (isExternalLinkReference(reference)) {
    type = 'external'
    url = reference.url || ''
  } else if (isWordbook(reference)) {
    type = 'wordbook'
    text = reference.value || ''
  } else if (reference?.__typename) {
    type = reference.__typename
  }

  if (!type) {
    return null
  }

  return {
    id: reference?.id || '',
    text,
    type,
    url,
  }
}
